import { isDefined } from "~/shared/common";
import { RelationshipKindKind } from "~/shared/enums";
import type { Relationship, RelationshipKind, Task, TaskAndUpdate } from "~/shared/types";
import { makeDuid } from "~/utils/common";
import { relationshipKindComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { setObjectMapAndSorted } from "./utils";

export type State = {
  _duidsToRelationshipKinds: Map<string, RelationshipKind>;
  _sortedRelationshipKindDuids: string[];
};

export const state: State = {
  _duidsToRelationshipKinds: new Map(),
  _sortedRelationshipKindDuids: [],
};

export type Getters = {
  relationshipKindList: RelationshipKind[];
  getRelationshipKindByDuid: (relationshipKindDuid: string) => RelationshipKind | undefined;
  getRelationshipKindByKind: (relationshipKindKind: RelationshipKindKind) => RelationshipKind;
  getRelationships: (task: Task, options?: { includeTrashed?: boolean }) => Relationship[];
  /* Get all of the relationships that have the given kind of relationship with the given task
  If forward is supplied, only relationships in that direction are returned, otherwise both directions. */
  getRelationshipsByKindKind: (
    task: Task,
    relationshipKindKind: RelationshipKindKind,
    forward?: boolean,
    options?: { includeTrashed?: boolean }
  ) => Relationship[];
  getParentTaskIfLoaded: (task: Task) => Task | undefined;
  getSourceOrTargetDuidsUiOnly: (
    task: Task,
    relationshipKindKind: RelationshipKindKind,
    source: boolean,
    extended: boolean
  ) => string[];
  /* Get all of the ancestors (parents, parents of parents, etc., but not the task itself) by DUID of a given task. */
  getAncestorDuidsUiOnly: (task: Task, options?: { includeTrashed?: boolean }) => string[];
  /* Get all of the descendants (children, children of children, etc., but not the task itself) by DUID of a given task. */
  getDescendantDuidsUiOnly: (task: Task) => string[];
  getSourcesOrTargets: (
    task: Task,
    relationshipKindKind: RelationshipKindKind,
    source: boolean,
    isExtended?: boolean
  ) => Promise<Task[]>;
  getSourcesOrTargetsExtended: (
    task: Task,
    relationshipKindKind: RelationshipKindKind,
    source: boolean
  ) => Promise<Task[]>;
  /* Get all of the ancestors (parents, parents of parents, etc., but not the task itself) by DUID of a given task. */
  getAncestors: (task: Task) => Promise<Task[]>;
  /* Get all of the descendants (children, children of children, etc., but not the task itself) by DUID of a given task. */
  getDescendants: (task: Task) => Promise<Task[]>;
};

export type Actions = {
  changeParent: (
    tasksAndUpdates: TaskAndUpdate[],
    newParent: Task | null,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => void;
  $setRelationshipKinds: (relationshipKinds: RelationshipKind[]) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  relationshipKindList() {
    return this._sortedRelationshipKindDuids.map((e) => this._duidsToRelationshipKinds.get(e)).filter(isDefined);
  },
  getRelationshipKindByDuid() {
    return (relationshipKindDuid) => this._duidsToRelationshipKinds.get(relationshipKindDuid);
  },
  getRelationshipKindByKind() {
    return (relationshipKindKind) => {
      const relationshipKind = this.relationshipKindList.filter((e) => e.kind === relationshipKindKind);
      if (relationshipKind.length !== 1) {
        throw new Error(`Expected exactly one relationship kind of kind ${relationshipKindKind}`);
      }
      return relationshipKind[0];
    };
  },
  getRelationships(s) {
    return (task, options = {}) => {
      const { includeTrashed = false } = options;
      return task.relationships.filter(({ targetDuid }) => {
        const target = [...s._duidsToTasks.values()].find(({ duid }) => targetDuid === duid);
        return target !== undefined && includeTrashed ? true : !target?.inTrash;
      });
    };
  },
  getRelationshipsByKindKind() {
    return (task, relationshipKindKind, forward, options = {}) => {
      const relationshipKindDuid = this.getRelationshipKindByKind(relationshipKindKind).duid;

      return this.getRelationships(task, options).filter(
        (relationship) =>
          relationship.kindDuid === relationshipKindDuid &&
          (forward === undefined || relationship.isForward === forward)
      );
    };
  },
  getParentTaskIfLoaded() {
    return (subtask) => {
      const parentTaskRelationship = this.getRelationshipsByKindKind(subtask, RelationshipKindKind.PARENT_OF, false)[0];
      if (!parentTaskRelationship) {
        return undefined;
      }

      return this.getTaskByDuidIfLoaded(parentTaskRelationship.targetDuid);
    };
  },
  getSourceOrTargetDuidsUiOnly() {
    return (task, relationshipKindKind, source) => {
      const result: string[] = [];
      const visited = new Set<string>();
      const queue: string[] = [task.duid];
      // limit BFS to 10000 to avoid while(true) loop
      for (let i = 0; i < 10000; i += 1) {
        if (queue.length === 0) {
          break;
        }
        const currDuid = queue.shift() as string;
        if (visited.has(currDuid)) {
          continue;
        }
        result.push(currDuid);
        visited.add(currDuid);
        const curr = this.getTaskByDuidIfLoaded(currDuid);
        if (!curr) {
          continue;
        }
        queue.push(...this.getRelationshipsByKindKind(curr, relationshipKindKind, !source).map((e) => e.targetDuid));
      }
      return result.slice(1);
    };
  },
  getAncestorDuidsUiOnly() {
    return (task, options) => {
      const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;

      const result: string[] = [];
      const visited = new Set<string>();
      let curr: Task = task;
      // limit linked list traversal to 1000 to avoid while(true) loop
      for (let i = 0; i < 1000; i += 1) {
        const currParentDuid = this.getRelationships(curr, options).find(
          (e) => e.kindDuid === parentKindDuid && !e.isForward
        )?.targetDuid;
        if (!currParentDuid || visited.has(currParentDuid)) {
          break;
        }
        const next = this.getTaskByDuidIfLoaded(currParentDuid);
        if (!next || next.drafterDuid) {
          break;
        }
        result.push(currParentDuid);
        visited.add(currParentDuid);
        curr = next;
      }
      return result.reverse();
    };
  },
  getDescendantDuidsUiOnly() {
    return (task) => this.getSourceOrTargetDuidsUiOnly(task, RelationshipKindKind.PARENT_OF, false, false);
  },
  getSourcesOrTargets() {
    return async (task, relationshipKindKind, source, isExtended = false) => {
      if (this.getRelationships(task).length === 0) {
        return [];
      }

      const tasks = await this.$batchers.taskRelationshipsManager.queryTaskRelationships({
        duid: makeDuid(),
        taskDuid: task.duid,
        relationshipKind: relationshipKindKind,
        source,
        isExtended,
      });

      if (!tasks) {
        return [];
      }
      tasks.forEach((e) => this.$createOrUpdateTask(e));
      return tasks;
    };
  },
  getSourcesOrTargetsExtended() {
    return async (task, relationshipKindKind, source) =>
      this.getSourcesOrTargets(task, relationshipKindKind, source, true);
  },
  getAncestors() {
    return async (task) => {
      const tasks = await this.getSourcesOrTargetsExtended(task, RelationshipKindKind.PARENT_OF, true);
      return tasks.slice(
        0,
        tasks.findIndex((e) => e.drafterDuid)
      );
    };
  },
  getDescendants() {
    return async (task) => {
      const tasks = await this.getSourcesOrTargetsExtended(task, RelationshipKindKind.PARENT_OF, false);
      return tasks;
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  changeParent(tasksAndUpdates, newParent, options) {
    const parentKindDuid = this.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;

    // TODO transactionify
    const updatedTasks: Task[] = [];
    tasksAndUpdates.forEach(({ task, order, partialTask }) => {
      const parentRelationship = this.getRelationshipsByKindKind(task, RelationshipKindKind.PARENT_OF, false)[0];
      const parentChange = (parentRelationship || newParent) && parentRelationship?.targetDuid !== newParent?.duid;
      if (!parentChange && order === task.order && !partialTask) {
        return;
      }
      if (parentChange) {
        updatedTasks.push(task);
      }
      const relationshipDelete =
        parentChange && parentRelationship
          ? { taskDuid: task.duid, relationshipDuid: parentRelationship.duid }
          : undefined;
      const relationshipCreate =
        parentChange && newParent
          ? {
              sourceDuid: newParent.duid,
              targetDuid: task.duid,
              relationshipKindDuid: parentKindDuid,
            }
          : undefined;
      this.deleteAndCreateRelationshipAndUpdateTask(
        relationshipDelete,
        relationshipCreate,
        {
          duid: task.duid,
          order,
          ...(partialTask ?? {}),
        },
        options
      );
    });
  },
  $setRelationshipKinds(relationshipKinds) {
    setObjectMapAndSorted(
      this._duidsToRelationshipKinds,
      this._sortedRelationshipKindDuids,
      relationshipKinds,
      relationshipKindComparator
    );
  },
};

export { actions, getters };
